import { useCallback, useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAuthToken } from "@convex-dev/auth/react";
import { useMutation, useQuery } from "convex/react";
import { MessageSquarePlus, TriangleAlert } from "lucide-react";

import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { AppArtifactView } from "@/components/apps/AppArtifactView";
import { StudioAppDetail } from "@/components/apps/StudioAppDetail";
import { StudioPending } from "@/components/apps/StudioPending";
import { ChatWindow } from "@/components/chat/ChatWindow";
import { Toast } from "@/components/ui/Toast";
import { useStudioApp } from "@/hooks/useStudioApps";
import {
  StudioShareError,
  usePublishAppShare,
  useShareForApp,
  useUnpublishAppShare,
} from "@/hooks/useStudioShares";

export const Route = createFileRoute("/_authenticated/studio/$appDir")({
  component: StudioAppDetailRoute,
});

const TOAST_MS = 3200;

/**
 * The per-app detail route (studio spec §4.4): data (app read, thread bind,
 * share state) lives here; the two-pane shell is pure
 * (`StudioAppDetail`) and its panes arrive as slots — the real `ChatWindow`
 * on the app's latest stamped thread (or the "Edit in chat" creator when no
 * thread is bound), and `AppArtifactView` on the live files.
 *
 * Tri-state on `useStudioApp`: loading → pane stub; NOT_FOUND / invalid
 * appDir → `StudioPending` (the app may still be mid-build — refresh is
 * manual, no timers); else the detail. On assistant turn end the preview
 * re-reads the vault (`refresh`) so edits land without a manual refresh.
 * Share wiring mirrors `AppArtifactContainer` (spec: the detail route wires
 * `AppArtifactView` from `useStudioShares` itself; publishing is open to
 * every plan — spec change log v1.0.1).
 */
function StudioAppDetailRoute() {
  const { appDir } = Route.useParams();
  const navigate = useNavigate();
  const authToken = useAuthToken();
  const { app, isLoading, error, errorCode, refresh } = useStudioApp(appDir);
  const thread = useQuery(api.threads.latestForStudioApp, { appDir });
  const share = useShareForApp(appDir);
  const publish = usePublishAppShare();
  const unpublish = useUnpublishAppShare();
  const createThread = useMutation(api.threads.create);
  const stampStudioApp = useMutation(api.threads.stampStudioApp);

  const [toast, setToast] = useState<string | null>(null);
  const [isShareBusy, setIsShareBusy] = useState(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
  }, []);

  const showToast = useCallback((message: string) => {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
  }, []);

  const runShareAction = useCallback(
    async (action: () => Promise<unknown>) => {
      setIsShareBusy(true);
      try {
        await action();
      } catch (err) {
        showToast(
          err instanceof StudioShareError
            ? err.userMessage
            : "Something went wrong. Please try again.",
        );
      } finally {
        setIsShareBusy(false);
      }
    },
    [showToast],
  );

  // "Edit in chat" creator: a fresh thread stamped for this surface AND this
  // app — `latestForStudioApp` is reactive, so the chat pane mounts the
  // moment the stamp lands (no navigation needed).
  const onEditInChat = useCallback(() => {
    void createThread({ title: `App: ${appDir}`, metadata: { source: "studio" } })
      .then((threadId) =>
        stampStudioApp({ threadId: threadId as Id<"threads">, appDir }),
      )
      .catch(() => showToast("Couldn't start a chat for this app — try again."));
  }, [createThread, stampStudioApp, appDir, showToast]);

  if (app === null) {
    if (isLoading) {
      return (
        <div className="flex h-[50vh] items-center justify-center font-body text-[13px] text-home-ink/45 dark:text-white/45">
          Loading app…
        </div>
      );
    }
    if (errorCode === "NOT_FOUND" || errorCode === "INVALID_APP_PATH") {
      return <StudioPending appDir={appDir} onRefresh={() => void refresh()} />;
    }
    return (
      <div className="flex h-[50vh] items-center justify-center px-6 text-center font-body text-[13px] text-home-ink/45 dark:text-white/45">
        {error ?? "This app isn't available."}
      </div>
    );
  }

  return (
    <>
      <StudioAppDetail
        name={app.name}
        onBack={() => void navigate({ to: "/studio" })}
        chatSlot={
          thread === undefined ? (
            <div className="flex flex-1 items-center justify-center font-body text-[13px] text-home-ink/45 dark:text-white/45">
              Loading…
            </div>
          ) : thread === null ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
              <p className="font-body text-[13px] text-home-ink/55 dark:text-white/55">
                No build chat is linked to this app yet.
              </p>
              <button
                type="button"
                onClick={onEditInChat}
                className="inline-flex min-h-[38px] items-center gap-1.5 rounded-full bg-home-yellow px-4 font-body text-[13px] font-bold text-home-ink shadow-[0_10px_28px_-12px_rgba(33,31,28,0.4)] transition-transform hover:scale-[1.02] active:scale-[0.98]"
              >
                <MessageSquarePlus className="h-4 w-4" strokeWidth={2.2} />
                Edit in chat
              </button>
            </div>
          ) : (
            <ChatWindow
              className="flex-1 overflow-hidden"
              threadId={thread._id}
              authToken={authToken}
              onAssistantTurnEnd={() => void refresh()}
            />
          )
        }
        previewSlot={
          <AppArtifactView
            app={app}
            onRefresh={() => void refresh()}
            share={{
              url: share?.url ?? null,
              isBusy: isShareBusy,
              onShare: () => void runShareAction(() => publish({ appDir })),
              onUnshare: () => void runShareAction(() => unpublish({ appDir })),
            }}
          />
        }
      />
      <Toast
        isVisible={toast !== null}
        message={toast ?? ""}
        icon={<TriangleAlert className="h-4 w-4 text-home-terracotta" />}
      />
    </>
  );
}
